import React, { useState, useMemo, useEffect } from 'react';
import { 
  MapPin, Calendar, Plus, Search, ArrowRight, 
  CheckCircle2, Clock, Truck, Trash2, Pencil, Loader2, 
  Undo2, LogOut, X, FileDown
} from 'lucide-react';
import CurrencyInput from 'react-currency-input-field';

import './App.css';

// Importações do Firebase
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged 
} from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  onSnapshot 
} from 'firebase/firestore';

/* eslint-disable no-undef, no-restricted-globals */

const firebaseConfig = JSON.parse(process.env.REACT_APP_FIREBASE_CONFIG);
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const appId = process.env.REACT_APP_APP_ID || 'mudancas-pro-prod';

export default function App() {
  const [user, setUser] = useState(null);
  const [moves, setMoves] = useState([]);
  const [loading, setLoading] = useState(true);

  // Controle de Interface
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCheckOutModalOpen, setIsCheckOutModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [checkOutId, setCheckOutId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('Todos');
  const [sortBy, setSortBy] = useState('status');
  const [sortOrder, setSortOrder] = useState('desc');
  const [detailMoveId, setDetailMoveId] = useState(null);

  const [formData, setFormData] = useState({
    name: '', role: '', origin: '', destination: '', date: '', cost: '', status: 'Agendado', observations: ''
  });

  const [checkOutData, setCheckOutData] = useState({
    finalCost: '',
    finalObservations: ''
  });

  // Estado da tela de login
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;
    const movesCollection = collection(db, 'artifacts', appId, 'public', 'data', 'moves');
    const unsubscribe = onSnapshot(movesCollection, (snapshot) => {
      const movesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Ordenação: Pendentes primeiro
      movesData.sort((a, b) => {
         const score = (status) => {
           if (status === 'Em Trânsito') return 3;
           if (status === 'Agendado') return 2;
           return 1;
         };
         return score(b.status) - score(a.status) || new Date(b.date || 0) - new Date(a.date || 0);
      });
      setMoves(movesData);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    if (!detailMoveId) return;
    const onKeyDown = (e) => { if (e.key === 'Escape') closeDetail(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [detailMoveId]);

  // --- LÓGICA DE NEGÓCIO ---

  const openDetail = (move) => setDetailMoveId(move.id);
  const closeDetail = () => setDetailMoveId(null);

  const handleRegisterExit = async (id) => {
    if (!confirm("Registrar saída desta mudança?")) return;
    try {
      const docRef = doc(db, 'artifacts', appId, 'public', 'data', 'moves', id);
      await updateDoc(docRef, {
        status: 'Em Trânsito',
        checkInTime: new Date().toISOString(),
        checkInBy: user?.email || user?.uid,
        lastUpdate: new Date().toISOString()
      });
    } catch (err) { alert("Erro ao registrar saída."); }
  };

  // Admin: Finalizar Chegada
  const handleFinalizeArrival = async (e) => {
    e.preventDefault();
    if (!checkOutId) return;

    try {
      const docRef = doc(db, 'artifacts', appId, 'public', 'data', 'moves', checkOutId);
      const finalCostNum = parseCurrency(checkOutData.finalCost);
      await updateDoc(docRef, {
        status: 'Concluído',
        finalCost: finalCostNum,
        checkOutTime: new Date().toISOString(),
        arrivalObservations: checkOutData.finalObservations,
        lastUpdate: new Date().toISOString()
      });
      setIsCheckOutModalOpen(false);
      setCheckOutId(null);
    } catch (err) { alert("Erro ao finalizar."); }
  };

  // Admin: REVERTER/CORRIGIR Status (Função "Fiz Errado")
  const handleRevertStatus = async (move) => {
    if (!confirm(`Deseja desfazer o status atual de "${move.status}"? O registro voltará para a etapa anterior.`)) return;
    
    let newStatus = 'Agendado';
    let updateData = {};

    if (move.status === 'Concluído') {
      newStatus = 'Em Trânsito';
      updateData = { 
        status: newStatus, 
        checkOutTime: null, // Limpa a hora de chegada
        finalCost: null 
      };
    } else if (move.status === 'Em Trânsito') {
      newStatus = 'Agendado';
      updateData = { 
        status: newStatus, 
        checkInTime: null // Limpa a hora de saída
      };
    }

    try {
      const docRef = doc(db, 'artifacts', appId, 'public', 'data', 'moves', move.id);
      await updateDoc(docRef, updateData);
    } catch (err) { alert("Erro ao reverter status."); }
  };

  const parseCurrency = (v) => {
    if (v === '' || v == null) return 0;
    const s = String(v).replace(',', '.');
    const n = parseFloat(s);
    return Number.isNaN(n) ? 0 : n;
  };

  const csvEscape = (v) => {
    if (v == null) return '';
    const s = String(v);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const moveToCsvRow = (move) => {
    const fmt = (d) => d ? new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '';
    return [
      csvEscape(move.name),
      csvEscape(move.role),
      csvEscape(move.origin),
      csvEscape(move.destination),
      csvEscape(move.date ? new Date(move.date).toLocaleDateString('pt-BR') : ''),
      csvEscape(move.status),
      move.cost != null && move.cost !== '' ? String(move.cost).replace('.', ',') : '',
      move.finalCost != null && move.finalCost !== '' ? String(move.finalCost).replace('.', ',') : '',
      csvEscape(move.observations),
      csvEscape(move.checkInTime ? fmt(move.checkInTime) : ''),
      csvEscape(move.checkOutTime ? fmt(move.checkOutTime) : ''),
      csvEscape(move.checkInBy),
      csvEscape(move.arrivalObservations),
      csvEscape(move.createdAt ? fmt(move.createdAt) : ''),
      csvEscape(move.lastUpdate || move.updatedAt ? fmt(move.lastUpdate || move.updatedAt) : '')
    ].join(';');
  };

  const CSV_HEADER = 'Nome;Cargo;Origem;Destino;Data;Status;Custo estimado;Custo final;Observações;Saiu em;Chegou em;Registrado por;Obs. chegada;Criado em;Última atualização';

  const downloadCsv = (filename, rows) => {
    const content = '\uFEFF' + CSV_HEADER + '\n' + rows.join('\n');
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportAllToCsv = () => {
    const rows = sortedFilteredMoves.map(moveToCsvRow);
    downloadCsv(`mudancas-${new Date().toISOString().slice(0, 10)}.csv`, rows);
  };

  const exportMoveToCsv = (move) => {
    downloadCsv(`mudanca-${(move.name || 'registro').replace(/\s+/g, '-')}-${move.id.slice(0, 8)}.csv`, [moveToCsvRow(move)]);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        cost: parseCurrency(formData.cost),
        updatedAt: new Date().toISOString()
      };
      if (editingId) {
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'moves', editingId), payload);
      } else {
        await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'moves'), { ...payload, createdAt: new Date().toISOString() });
      }
      resetForm();
    } catch (err) {
      console.error(err);
      const code = err?.code ? ` Código: ${err.code}.` : '';
      alert(`Erro ao salvar.${code}`);
    }
  };

  const handleDelete = async (id) => {
    if (confirm('Remover registro permanentemente?')) {
      await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'moves', id));
    }
  };

  const resetForm = () => {
    setFormData({ name: '', role: '', origin: '', destination: '', date: '', cost: '', status: 'Agendado', observations: '' });
    setEditingId(null);
    setIsModalOpen(false);
  };

  const handleEditClick = (move) => {
    setFormData({ ...move, cost: move.cost != null ? String(move.cost) : '' });
    setEditingId(move.id);
    setIsModalOpen(true);
    closeDetail();
  };

  const openCheckOutFromDetail = (move) => {
    setCheckOutId(move.id);
    setCheckOutData({ finalCost: move.cost != null ? String(move.cost) : '', finalObservations: '' });
    setIsCheckOutModalOpen(true);
    closeDetail();
  };

  const filteredMoves = moves.filter(move => {
    const matchesSearch = (move.name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'Todos' || move.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const statusPriority = (status) => {
    if (status === 'Agendado') return 3;
    if (status === 'Em Trânsito') return 2;
    return 1;
  };

  const sortedFilteredMoves = useMemo(() => {
    const list = [...filteredMoves];
    const mult = sortOrder === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      if (sortBy === 'name') {
        return mult * (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase(), 'pt-BR');
      }
      if (sortBy === 'date') {
        const da = new Date(a.date || 0).getTime();
        const db = new Date(b.date || 0).getTime();
        return mult * (da - db);
      }
      if (sortBy === 'createdAt') {
        const ta = new Date(a.createdAt || a.lastUpdate || 0).getTime();
        const tb = new Date(b.createdAt || b.lastUpdate || 0).getTime();
        return mult * (ta - tb);
      }
      if (sortBy === 'status') {
        const sa = statusPriority(a.status);
        const sb = statusPriority(b.status);
        if (sa !== sb) return mult * (sb - sa);
        return mult * (new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime());
      }
      return 0;
    });
    return list;
  }, [filteredMoves, sortBy, sortOrder]);

  const displayName = user?.displayName || (user?.email ? (() => {
    const part = user.email.split('@')[0] || '';
    const nameMap = { 'raissa.aquino': 'Raissa', 'danilo.duarte': 'Danilo' };
    return nameMap[part] || part.split('.').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ') || part;
  })() : '');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
    } catch (err) {
      setLoginError(err?.message || err?.code || 'Erro ao entrar.');
      setLoginLoading(false);
    }
  };

  const stats = useMemo(() => {
    const totalCost = moves.reduce((acc, curr) => acc + (Number(curr.finalCost || curr.cost) || 0), 0);
    return { 
      total: moves.length, 
      pending: moves.filter(m => m.status === 'Agendado').length,
      inTransit: moves.filter(m => m.status === 'Em Trânsito').length,
      completed: moves.filter(m => m.status === 'Concluído').length,
      totalCost 
    };
  }, [moves]);

  // --- UI Components ---

  const StatusBadge = ({ status }) => {
    let color = 'bg-gray-100 text-gray-800';
    let icon = <Clock className="w-3 h-3" />;
    if (status === 'Concluído') { color = 'bg-green-100 text-green-800'; icon = <CheckCircle2 className="w-3 h-3" />; }
    else if (status === 'Em Trânsito') { color = 'bg-blue-100 text-blue-800'; icon = <Truck className="w-3 h-3" />; }
    else if (status === 'Agendado') { color = 'bg-yellow-100 text-yellow-800'; icon = <Calendar className="w-3 h-3" />; }
    return <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>{icon} {status}</span>;
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-blue-600" /></div>;

  if (!user) {
    return (
      <div className="min-h-screen font-sans bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-sm border border-gray-200">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-blue-600">
              <Truck className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">Gestão de Mudanças</h1>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
              <input type="email" required className="w-full border border-gray-300 p-2 rounded-lg" placeholder="seu@email.com" value={loginEmail} onChange={e => { setLoginEmail(e.target.value); setLoginError(''); }} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Senha</label>
              <input type="password" required className="w-full border border-gray-300 p-2 rounded-lg" placeholder="••••••••" value={loginPassword} onChange={e => { setLoginPassword(e.target.value); setLoginError(''); }} />
            </div>
            {loginError && <p className="text-sm text-red-600">{loginError}</p>}
            <button type="submit" disabled={loginLoading} className="w-full bg-blue-600 text-white py-2 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50">
              {loginLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Entrar
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen font-sans bg-gray-50">
      
      {/* HEADER */}
      <header className="bg-white text-gray-900 shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-600">
              <Truck className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Gestão de Mudanças</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600">Boas vindas, <strong>{displayName}</strong></span>
            <button onClick={() => { resetForm(); setIsModalOpen(true); }} className="bg-blue-600 text-white px-3 py-2 rounded-lg text-sm flex gap-2"><Plus className="w-4 h-4"/> Novo</button>
            <button onClick={() => signOut(auth)} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm" title="Sair"><LogOut className="w-4 h-4"/> Sair</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-l-4 border-l-yellow-500">
            <div className="text-gray-500 text-xs uppercase font-bold">Aguardando Saída</div>
            <div className="text-2xl font-bold">{stats.pending}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-l-4 border-l-blue-500">
            <div className="text-gray-500 text-xs uppercase font-bold">Em Trânsito</div>
            <div className="text-2xl font-bold">{stats.inTransit}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-l-4 border-l-green-500">
            <div className="text-gray-500 text-xs uppercase font-bold">Finalizados</div>
            <div className="text-2xl font-bold">{stats.completed}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="text-gray-500 text-xs uppercase font-bold">Custo Total</div>
            <div className="text-xl font-bold text-gray-900">{stats.totalCost.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>
          </div>
        </div>

        {/* BUSCA, FILTRO E ORDENAÇÃO */}
        <div className="mb-6 flex flex-wrap gap-4 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
            <input type="text" placeholder="Buscar..." className="w-full pl-10 pr-4 py-2 border rounded-lg" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          </div>
          <select className="border rounded-lg px-4 py-2" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="Todos">Todos Status</option>
            <option value="Agendado">Agendado</option>
            <option value="Em Trânsito">Em Trânsito</option>
            <option value="Concluído">Concluído</option>
          </select>
          <div className="flex flex-wrap gap-2 items-center">
            <label htmlFor="sort-by" className="text-sm text-gray-600 whitespace-nowrap">Ordenar por</label>
            <select id="sort-by" className="border rounded-lg px-3 py-2 text-sm" value={sortBy} onChange={e => setSortBy(e.target.value)}>
              <option value="status">Status</option>
              <option value="name">Nome</option>
              <option value="date">Data da mudança</option>
              <option value="createdAt">Data de registro</option>
            </select>
            <select className="border rounded-lg px-3 py-2 text-sm" value={sortOrder} onChange={e => setSortOrder(e.target.value)} aria-label="Ordem">
              <option value="desc">
                {sortBy === 'name' ? 'Z → A' : sortBy === 'status' ? 'Pendentes primeiro' : 'Mais recente primeiro'}
              </option>
              <option value="asc">
                {sortBy === 'name' ? 'A → Z' : sortBy === 'status' ? 'Concluídos primeiro' : 'Mais antigo primeiro'}
              </option>
            </select>
          </div>
          <button type="button" onClick={exportAllToCsv} className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700" title="Exportar lista para planilha">
            <FileDown className="w-4 h-4"/> Exportar lista
          </button>
        </div>

        {/* TABELA */}
        <div className="bg-white rounded-xl shadow border overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-6 py-4 text-left">Detalhes</th>
                <th className="px-6 py-4 text-left">Status</th>
                <th className="px-6 py-4 text-center">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {sortedFilteredMoves.length > 0 ? sortedFilteredMoves.map(move => (
                <tr key={move.id} className="hover:bg-gray-50 align-middle">
                  <td className="px-6 py-4 align-middle">
                    <button type="button" onClick={() => openDetail(move)} className="text-left w-full hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded">
                      <div className="font-bold text-gray-900">{move.name}</div>
                      <div className="text-sm text-gray-500 flex gap-2 items-center">
                        <MapPin className="w-3 h-3"/> {move.origin} <ArrowRight className="w-3 h-3"/> {move.destination}
                      </div>
                      {move.observations && <div className="text-xs text-amber-600 mt-1">Obs: {move.observations}</div>}
                    </button>
                  </td>
                  <td className="px-6 py-4 align-middle">
                    <div className="inline-flex flex-col items-start gap-1">
                      <StatusBadge status={move.status} />
                      {move.checkInTime && <span className="text-xs text-gray-400">Saiu: {new Date(move.checkInTime).toLocaleTimeString('pt-BR', {hour:'2-digit', minute:'2-digit'})}</span>}
                      {move.checkOutTime && <span className="text-xs text-gray-400">Chegou: {new Date(move.checkOutTime).toLocaleTimeString('pt-BR', {hour:'2-digit', minute:'2-digit'})}</span>}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center align-middle">
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                      {move.status === 'Agendado' && (
                        <button
                          onClick={() => handleRegisterExit(move.id)}
                          className="py-2 px-4 rounded text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white shadow-md transition-all"
                        >
                          Registrar saída
                        </button>
                      )}
                      {move.status === 'Em Trânsito' && (
                        <button onClick={() => { setCheckOutId(move.id); setCheckOutData({ finalCost: move.cost != null ? String(move.cost) : '', finalObservations: '' }); setIsCheckOutModalOpen(true); }} className="bg-green-600 text-white p-2 rounded shadow hover:bg-green-700" title="Finalizar Chegada">
                          <CheckCircle2 className="w-4 h-4"/>
                        </button>
                      )}
                      {(move.status !== 'Agendado') && (
                        <button onClick={() => handleRevertStatus(move)} className="bg-orange-100 text-orange-700 p-2 rounded hover:bg-orange-200" title="Desfazer Status (Corrigir)">
                          <Undo2 className="w-4 h-4"/>
                        </button>
                      )}
                      <button onClick={() => handleEditClick(move)} className="text-gray-400 hover:text-blue-600 p-2" title="Editar"><Pencil className="w-4 h-4"/></button>
                      <button onClick={() => handleDelete(move.id)} className="text-gray-400 hover:text-red-600 p-2" title="Excluir"><Trash2 className="w-4 h-4"/></button>
                    </div>
                  </td>
                </tr>
              )) : <tr><td colSpan="3" className="p-8 text-center text-gray-500">Nenhum registro encontrado.</td></tr>}
            </tbody>
          </table>
        </div>
      </main>

      {/* MODAL EDITAR (Admin) */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="font-bold text-lg mb-4">{editingId ? 'Editar' : 'Nova'} Mudança</h3>
            <form onSubmit={handleSave} className="space-y-3">
              <input required className="w-full border p-2 rounded" placeholder="Nome" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})}/>
              <input className="w-full border p-2 rounded" placeholder="Cargo" value={formData.role} onChange={e => setFormData({...formData, role: e.target.value})}/>
              <div className="flex gap-2">
                 <input className="w-full border p-2 rounded" placeholder="Origem" value={formData.origin} onChange={e => setFormData({...formData, origin: e.target.value})}/>
                 <input className="w-full border p-2 rounded" placeholder="Destino" value={formData.destination} onChange={e => setFormData({...formData, destination: e.target.value})}/>
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <label htmlFor="form-date" className="block text-xs text-gray-500 mb-1">Data de início da mudança</label>
                  <input id="form-date" type="date" className="w-full border p-2 rounded" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})}/>
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">Valor estimado (R$)</label>
                  <div className="flex border rounded overflow-hidden">
                    <span className="inline-flex items-center px-3 bg-gray-100 text-gray-600 border-r">R$</span>
                    <CurrencyInput
                      className="w-full p-2 min-w-0"
                      value={formData.cost === '' || formData.cost == null ? '' : String(formData.cost)}
                      onValueChange={(value) => setFormData({ ...formData, cost: value ?? '' })}
                      decimalsLimit={2}
                      decimalSeparator=","
                      groupSeparator="."
                      placeholder="0,00"
                    />
                  </div>
                </div>
              </div>
              
              {/* ADMIN PODE FORÇAR STATUS AQUI TAMBÉM */}
              {editingId && (
                <div>
                  <label className="text-xs text-gray-500">Forçar Status (Correção Manual)</label>
                  <select className="w-full border p-2 rounded" value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})}>
                    <option value="Agendado">Agendado</option>
                    <option value="Em Trânsito">Em Trânsito</option>
                    <option value="Concluído">Concluído</option>
                  </select>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <button type="button" onClick={resetForm} className="flex-1 border p-2 rounded">Cancelar</button>
                <button type="submit" className="flex-1 bg-blue-600 text-white p-2 rounded">Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL CHECK-OUT (Admin) */}
      {isCheckOutModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm border-t-4 border-green-600">
            <h3 className="font-bold text-lg mb-2">Finalizar Mudança</h3>
            <p className="text-sm text-gray-500 mb-4">Confirme os valores finais de chegada.</p>
            <form onSubmit={handleFinalizeArrival} className="space-y-3">
              <div>
                <label className="text-xs font-bold text-gray-700">Custo Final Real</label>
                <div className="flex border-2 border-green-200 focus-within:border-green-600 rounded overflow-hidden">
                  <span className="inline-flex items-center px-3 bg-green-50 text-gray-700 border-r">R$</span>
                  <CurrencyInput
                    className="w-full p-2 text-lg font-bold min-w-0 focus:outline-none"
                    value={checkOutData.finalCost === '' || checkOutData.finalCost == null ? '' : String(checkOutData.finalCost)}
                    onValueChange={(value) => setCheckOutData({ ...checkOutData, finalCost: value ?? '' })}
                    decimalsLimit={2}
                    decimalSeparator=","
                    groupSeparator="."
                    placeholder="0,00"
                  />
                </div>
              </div>
              <textarea className="w-full border p-2 rounded text-sm" placeholder="Observações finais..." value={checkOutData.finalObservations} onChange={e => setCheckOutData({...checkOutData, finalObservations: e.target.value})}/>
              <div className="flex gap-2">
                <button type="button" onClick={() => setIsCheckOutModalOpen(false)} className="flex-1 border p-2 rounded">Cancelar</button>
                <button type="submit" className="flex-1 bg-green-600 text-white p-2 rounded font-bold">Encerrar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL DETALHES (somente leitura) */}
      {detailMoveId && (() => {
        const move = moves.find(m => m.id === detailMoveId);
        if (!move) return null;
        const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pt-BR') : '—';
        const fmtDateTime = (d) => d ? new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';
        const fmtCurrency = (v) => (v != null && v !== '') ? Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';
        const Field = ({ label, value }) => (
          <div className="min-w-0"><span className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-0.5">{label}</span><div className="text-gray-900 text-sm">{value}</div></div>
        );
        return (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && closeDetail()} role="dialog" aria-modal="true" aria-labelledby="detail-title">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
              {/* Header fixo: título + X sempre visível */}
              <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b bg-white">
                <h3 id="detail-title" className="font-bold text-lg">Detalhes da mudança</h3>
                <button type="button" onClick={closeDetail} className="p-2 -m-2 rounded-lg hover:bg-gray-100 text-gray-600" aria-label="Fechar"><X className="w-5 h-5"/></button>
              </div>
              {/* Conteúdo rolável */}
              <div className="flex-1 overflow-y-auto px-6 py-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                  <div className="sm:col-span-2 space-y-4"><Field label="Nome" value={move.name || '—'} /><Field label="Cargo" value={move.role || '—'} /></div>
                  <Field label="Origem" value={move.origin || '—'} />
                  <Field label="Destino" value={move.destination || '—'} />
                  <Field label="Data da mudança" value={fmtDate(move.date)} />
                  <Field label="Status" value={<StatusBadge status={move.status} />} />
                  <Field label="Custo estimado" value={fmtCurrency(move.cost)} />
                  <Field label="Custo final" value={fmtCurrency(move.finalCost)} />
                  <div className="sm:col-span-2"><Field label="Observações" value={move.observations || '—'} /></div>
                  <Field label="Saiu em" value={fmtDateTime(move.checkInTime)} />
                  <Field label="Chegou em" value={fmtDateTime(move.checkOutTime)} />
                  <Field label="Registrado por" value={move.checkInBy || '—'} />
                  <Field label="Observações de chegada" value={move.arrivalObservations || '—'} />
                  <Field label="Criado em" value={fmtDateTime(move.createdAt)} />
                  <Field label="Última atualização" value={fmtDateTime(move.lastUpdate || move.updatedAt)} />
                </div>
              </div>
              {/* Footer fixo */}
              <div className="flex-shrink-0 flex flex-wrap gap-2 px-6 py-4 border-t bg-gray-50">
                <button type="button" onClick={closeDetail} className="px-4 py-2 border rounded-lg bg-white hover:bg-gray-50">Fechar</button>
                {move.status === 'Agendado' && <button type="button" onClick={() => handleRegisterExit(move.id)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Registrar saída</button>}
                {move.status === 'Em Trânsito' && <button type="button" onClick={() => openCheckOutFromDetail(move)} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">Finalizar chegada</button>}
                <button type="button" onClick={() => handleEditClick(move)} className="px-4 py-2 text-blue-600 border border-blue-600 rounded-lg bg-white hover:bg-blue-50">Editar</button>
                <button type="button" onClick={() => exportMoveToCsv(move)} className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 flex items-center gap-2" title="Exportar este registro">
                  <FileDown className="w-4 h-4"/> Exportar
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}